import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useIsPro } from "@/hooks/useIsPro";
import { usePaywallPreview } from "@/hooks/usePaywallPreview";
import { usePaywallModal } from "@/contexts/PaywallModalContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  subDays,
  subMonths,
  subYears,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
} from "date-fns";
import {
  Package,
  AlertTriangle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ChartContainer } from "@/components/ui/chart";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
  ReferenceLine,
  Area,
  ComposedChart,
} from "recharts";
import AIInsights, { type TimeRange } from "@/components/AIInsights";
import { AITipCard } from "@/components/AITipCard";
import { useBranches } from "@/hooks/useBranches";
import { useBranchContext } from "@/contexts/BranchContext";
import { useSubscriptionPlan } from "@/hooks/useSubscriptionPlan";
import { MapPin } from "lucide-react";

const INCOME_COLOR = "#228B22";
const EXPENSE_COLOR = "#DC143C";
const NET_FLOW_COLOR = "#4169E1";
const NEGATIVE_BURN_FILL = "rgba(255, 180, 180, 0.4)";
const NET_FLOW_FILL = "rgba(65, 105, 225, 0.15)";

const TIMEZONE_OFFSET_MS = 2 * 60 * 60 * 1000; // Zambia (UTC+2)

function toLocalStartOfDayUTC(date: Date): Date {
  const t = date.getTime() + TIMEZONE_OFFSET_MS;
  const d = new Date(t);
  const y = d.getUTCFullYear(),
    m = d.getUTCMonth(),
    day = d.getUTCDate();
  return new Date(Date.UTC(y, m, day, 0, 0, 0, 0) - TIMEZONE_OFFSET_MS);
}

function toLocalEndOfDayUTC(date: Date): Date {
  const start = toLocalStartOfDayUTC(date);
  return new Date(start.getTime() + 24 * 60 * 60 * 1000 - 1);
}

function toZambiaMonthStart(d: Date): Date {
  const t = d.getTime() + TIMEZONE_OFFSET_MS;
  const x = new Date(t);
  const y = x.getUTCFullYear(),
    m = x.getUTCMonth();
  return new Date(Date.UTC(y, m, 1, 0, 0, 0, 0) - TIMEZONE_OFFSET_MS);
}

function toZambiaMonthEnd(d: Date): Date {
  const next = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1, 0, 0, 0, 0) - TIMEZONE_OFFSET_MS);
  return new Date(next.getTime() - 1);
}

function getRangeBounds(range: TimeRange) {
  const now = new Date();
  let currentStart: Date;
  let currentEnd: Date;
  let previousStart: Date;
  let previousEnd: Date;
  let fetchSince: Date;

  switch (range) {
    case "7D": {
      currentEnd = toLocalEndOfDayUTC(now);
      currentStart = toLocalStartOfDayUTC(subDays(now, 6));
      previousEnd = new Date(currentStart.getTime() - 1);
      previousStart = toLocalStartOfDayUTC(subDays(now, 13));
      fetchSince = subDays(previousStart, 1);
      break;
    }
    case "1M": {
      currentEnd = toLocalEndOfDayUTC(now);
      currentStart = toZambiaMonthStart(subMonths(now, 1));
      previousEnd = new Date(currentStart.getTime() - 1);
      previousStart = toZambiaMonthStart(subMonths(now, 2));
      fetchSince = subMonths(previousStart, 1);
      break;
    }
    case "3M":
    case "6M": {
      const months = range === "3M" ? 3 : 6;
      currentEnd = toLocalEndOfDayUTC(now);
      currentStart = toZambiaMonthStart(subMonths(now, months));
      previousEnd = new Date(currentStart.getTime() - 1);
      previousStart = toZambiaMonthStart(subMonths(now, months * 2));
      fetchSince = subMonths(previousStart, 1);
      break;
    }
    case "1Y": {
      currentEnd = toLocalEndOfDayUTC(now);
      currentStart = new Date(Date.UTC(subYears(now, 1).getUTCFullYear(), 0, 1, 0, 0, 0, 0) - TIMEZONE_OFFSET_MS);
      previousEnd = new Date(currentStart.getTime() - 1);
      previousStart = new Date(Date.UTC(subYears(now, 2).getUTCFullYear(), 0, 1, 0, 0, 0, 0) - TIMEZONE_OFFSET_MS);
      fetchSince = subYears(previousStart, 1);
      break;
    }
    case "All":
    default: {
      currentEnd = toLocalEndOfDayUTC(now);
      currentStart = new Date(Date.UTC(subYears(now, 10).getUTCFullYear(), 0, 1, 0, 0, 0, 0) - TIMEZONE_OFFSET_MS);
      previousEnd = new Date(currentStart.getTime() - 1);
      previousStart = new Date(Date.UTC(subYears(now, 20).getUTCFullYear(), 0, 1, 0, 0, 0, 0) - TIMEZONE_OFFSET_MS);
      fetchSince = subYears(previousStart, 1);
      break;
    }
  }

  return { currentStart, currentEnd, previousStart, previousEnd, fetchSince };
}

const TIME_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: "7D", label: "7D" },
  { value: "1M", label: "1M" },
  { value: "3M", label: "3M" },
  { value: "6M", label: "6M" },
  { value: "1Y", label: "1Y" },
  { value: "All", label: "All" },
];

type SaleRow = {
  id: string;
  final_amount: number | null;
  total_price: number | null;
  quantity: number | null;
  unit_price: number | null;
  created_at: string;
  item_name: string | null;
  business_id?: string | null;
  item_id?: string | null;
  branch_id?: string | null;
};
type ExpenseRow = { id: string; amount: number; expense_date: string; created_at?: string; business_id?: string | null; branch_id?: string | null };

function saleAmount(s: SaleRow): number {
  const amt = s.final_amount ?? s.total_price ?? (s.quantity && s.unit_price ? s.quantity * s.unit_price : 0);
  return Number(amt || 0);
}

function inRange(iso: string, start: Date, end: Date): boolean {
  const t = new Date(iso).getTime();
  return t >= start.getTime() && t <= end.getTime();
}

function getZambiaNow(): Date {
  return new Date(Date.now() + TIMEZONE_OFFSET_MS);
}

function getGreeting(): "morning" | "afternoon" | "evening" {
  const hour = (new Date().getUTCHours() + 2) % 24;
  if (hour < 12) return "morning";
  if (hour < 17) return "afternoon";
  return "evening";
}

function formatZambiaDateTime(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  const h = String(date.getUTCHours()).padStart(2, "0");
  const min = String(date.getUTCMinutes()).padStart(2, "0");
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const dayName = days[date.getUTCDay()];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthName = months[date.getUTCMonth()];
  return `${h}:${min} · ${dayName}, ${monthName} ${d}, ${y}`;
}

function getFirstName(fullName: string | null | undefined): string {
  if (!fullName?.trim()) return "";
  const first = fullName.trim().split(/\s+/)[0];
  return first || "";
}

export default function NexusDashboard() {
  const { businessId, profile } = useAuth();
  const firstName = getFirstName(profile?.full_name ?? undefined);
  const greeting = getGreeting();
  const [currentTime, setCurrentTime] = useState(() => formatZambiaDateTime(getZambiaNow()));
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(formatZambiaDateTime(getZambiaNow())), 60_000);
    return () => clearInterval(t);
  }, []);
  const isPro = useIsPro();
  const { data: paywallPreview, refetch: refetchPaywallPreview } = usePaywallPreview();
  useEffect(() => {
    refetchPaywallPreview();
  }, [refetchPaywallPreview]);
  const { openPaywall } = usePaywallModal();
  const { branches } = useBranches();
  const { selectedBranchId } = useBranchContext();
  const plan = useSubscriptionPlan();
  const [timeRange, setTimeRange] = useState<TimeRange>("7D");
  const [sales, setSales] = useState<SaleRow[]>([]);
  const [expenses, setExpenses] = useState<ExpenseRow[]>([]);
  const [inventory, setInventory] = useState<{ name: string; sku: string; current_stock: number; min_threshold: number | null }[]>([]);
  const [inventoryWithCost, setInventoryWithCost] = useState<{ id: string; name: string; current_stock: number; buying_price: number | null }[]>([]);
  const [sales30dItemIds, setSales30dItemIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [branchStats, setBranchStats] = useState<{ branchId: string; itemCount: number; criticalCount: number }[]>([]);

  const bounds = useMemo(() => getRangeBounds(timeRange), [timeRange]);

  const fetchDashboardData = useCallback(async () => {
    if (!businessId) {
      setLoading(false);
      return;
    }

    const startDate = bounds.fetchSince;
    const endDate = bounds.currentEnd;
    const startDateIso = startDate.toISOString();
    const endDateIso = endDate.toISOString();
    const startDateStr = startDateIso.slice(0, 10);
    const endDateStr = endDateIso.slice(0, 10);

    setLoading(true);
    try {
    const branchFilter = selectedBranchId !== "all" ? selectedBranchId : null;
    let salesSelect = supabase
      .from("sales")
      .select("id, final_amount, total_price, quantity, unit_price, created_at, item_name, business_id, item_id, branch_id")
      .gte("created_at", startDateIso)
      .lte("created_at", endDateIso)
      .order("created_at", { ascending: true });
    let expensesSelect = supabase
      .from("expenses")
      .select("id, amount, expense_date, business_id, branch_id")
      .gte("expense_date", startDateStr)
      .lte("expense_date", endDateStr)
      .order("expense_date", { ascending: true });
    let invQuery = supabase.from("inventory").select("name, sku, current_stock, min_threshold, branch_id");
    if (branchFilter) {
      salesSelect = salesSelect.eq("branch_id", branchFilter);
      expensesSelect = expensesSelect.eq("branch_id", branchFilter);
      invQuery = invQuery.eq("branch_id", branchFilter);
    }

    const [salesRes, expensesRes, invRes] = await Promise.all([
      salesSelect.eq("business_id", businessId),
      expensesSelect.eq("business_id", businessId),
      invQuery.eq("business_id", businessId),
    ]);
    const salesData: SaleRow[] = salesRes.data ? (salesRes.data as SaleRow[]) : [];
    const expensesData: ExpenseRow[] = expensesRes.data ? (expensesRes.data as ExpenseRow[]) : [];
    if (invRes.data) setInventory(invRes.data as typeof inventory);

    setSales(salesData);
    setExpenses(expensesData);

    // Stock Intelligence: buying_price + current_stock from inventory
    let invWithCostQuery = supabase.from("inventory").select("id, name, current_stock, buying_price, branch_id");
    if (branchFilter) invWithCostQuery = invWithCostQuery.eq("branch_id", branchFilter);
    let sales30Query = supabase
      .from("sales")
      .select("item_id")
      .gte("created_at", new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
      .not("item_id", "is", null);
    if (branchFilter) sales30Query = sales30Query.eq("branch_id", branchFilter);
    const [invCostRes, sales30Res] = await Promise.all([
      invWithCostQuery.eq("business_id", businessId),
      sales30Query.eq("business_id", businessId),
    ]);
    if (invCostRes.data) setInventoryWithCost(invCostRes.data as { id: string; name: string; current_stock: number; buying_price: number | null }[]);
    if (sales30Res.data) {
      const ids = new Set((sales30Res.data as { item_id: string | null }[]).map((r) => r.item_id).filter(Boolean) as string[]);
      setSales30dItemIds(ids);
    }
    } finally {
      setLoading(false);
    }
  }, [businessId, bounds.fetchSince, bounds.currentEnd, selectedBranchId]);

  // Per-branch stats when multi-branch (Pro Growth)
  useEffect(() => {
    if (!businessId || branches.length <= 1) {
      setBranchStats([]);
      return;
    }
    const q = supabase.from("inventory").select("branch_id, current_stock, min_threshold").not("branch_id", "is", null);
    q.eq("business_id", businessId).then(({ data }) => {
      if (!data) return;
      const byBranch = new Map<string, { itemCount: number; criticalCount: number }>();
      (data as { branch_id: string; current_stock: number; min_threshold: number | null }[]).forEach((row) => {
        const b = byBranch.get(row.branch_id) ?? { itemCount: 0, criticalCount: 0 };
        b.itemCount += 1;
        if (row.current_stock <= (row.min_threshold ?? 0)) b.criticalCount += 1;
        byBranch.set(row.branch_id, b);
      });
      setBranchStats(
        Array.from(byBranch.entries()).map(([branchId, s]) => ({ branchId, itemCount: s.itemCount, criticalCount: s.criticalCount }))
      );
    });
  }, [businessId, branches.length]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    const onFocus = () => fetchDashboardData();
    const onVisibility = () => { if (document.visibilityState === "visible") fetchDashboardData(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchDashboardData]);

  // Same data source as Master Ledger: Revenue from sales, Expenses from expenses table
  const { currentSales, previousSales, currentExpenses, previousExpenses } = useMemo(() => {
    const curS = sales.filter((s) => inRange(s.created_at, bounds.currentStart, bounds.currentEnd));
    const prevS = sales.filter((s) => inRange(s.created_at, bounds.previousStart, bounds.previousEnd));
    const curE = expenses.filter((e) => {
      const t = new Date(e.expense_date).getTime();
      return t >= bounds.currentStart.getTime() && t <= bounds.currentEnd.getTime();
    });
    const prevE = expenses.filter((e) => {
      const t = new Date(e.expense_date).getTime();
      return t >= bounds.previousStart.getTime() && t <= bounds.previousEnd.getTime();
    });
    return { currentSales: curS, previousSales: prevS, currentExpenses: curE, previousExpenses: prevE };
  }, [sales, expenses, bounds]);

  const currentRevenue = useMemo(() => currentSales.reduce((s, x) => s + saleAmount(x), 0), [currentSales]);
  const previousRevenue = useMemo(() => previousSales.reduce((s, x) => s + saleAmount(x), 0), [previousSales]);
  const currentExpTotal = useMemo(() => currentExpenses.reduce((s, e) => s + Number(e.amount ?? 0), 0), [currentExpenses]);
  const previousExpTotal = useMemo(() => previousExpenses.reduce((s, e) => s + Number(e.amount ?? 0), 0), [previousExpenses]);

  const costMap = useMemo(() => {
    const m = new Map<string, number>();
    inventoryWithCost.forEach((i) => m.set(i.id, i.buying_price ?? 0));
    return m;
  }, [inventoryWithCost]);
  const currentCogs = useMemo(
    () => currentSales.reduce((s, x) => s + (Number(x.quantity) || 0) * (x.item_id ? costMap.get(x.item_id) ?? 0 : 0), 0),
    [currentSales, costMap]
  );
  const previousCogs = useMemo(
    () => previousSales.reduce((s, x) => s + (Number(x.quantity) || 0) * (x.item_id ? costMap.get(x.item_id) ?? 0 : 0), 0),
    [previousSales, costMap]
  );

  const currentProfit = currentRevenue - currentCogs - currentExpTotal;
  const previousProfit = previousRevenue - previousCogs - previousExpTotal;
  const currentMargin = currentRevenue > 0 ? (currentProfit / currentRevenue) * 100 : (currentRevenue === 0 && currentExpTotal > 0 ? -100 : 0);
  const previousMargin = previousRevenue > 0 ? (previousProfit / previousRevenue) * 100 : (previousRevenue === 0 && previousExpTotal > 0 ? -100 : 0);

  const hasPreviousData = previousRevenue > 0 || previousExpTotal > 0;

  const pctChange = (curr: number, prev: number): number | null => {
    if (!hasPreviousData) return null; // No previous period data — can't compare
    if (prev === 0) return curr === 0 ? 0 : 100;
    return ((curr - prev) / prev) * 100;
  };

  const revPct = pctChange(currentRevenue, previousRevenue);
  const expPct = pctChange(currentExpTotal, previousExpTotal);
  const profitPct = pctChange(currentProfit, previousProfit);
  const marginPct = pctChange(currentMargin, previousMargin);

  const formatPct = (p: number | null) => {
    if (p == null) return "New";
    return `${p >= 0 ? "+" : ""}${p.toFixed(1)}%`;
  };

  const deadStock = useMemo(
    () => inventoryWithCost.filter((i) => !sales30dItemIds.has(i.id)),
    [inventoryWithCost, sales30dItemIds]
  );

  const branchComparisonMetrics = useMemo(() => {
    if (selectedBranchId !== "all" || branches.length <= 1) return [];
    const branchIdToName = new Map(branches.map((b) => [b.id, b.name ?? "Branch"]));
    const byBranch = new Map<string, { revenue: number; expenses: number; deadStockCount: number; deadStockValue: number; itemCount: number }>();
    branches.forEach((b) => byBranch.set(b.id, { revenue: 0, expenses: 0, deadStockCount: 0, deadStockValue: 0, itemCount: 0 }));
    currentSales.forEach((s) => {
      const bid = s.branch_id ?? "";
      if (byBranch.has(bid)) {
        const m = byBranch.get(bid)!;
        m.revenue += saleAmount(s);
        byBranch.set(bid, m);
      }
    });
    currentExpenses.forEach((e) => {
      const bid = e.branch_id ?? "";
      if (byBranch.has(bid)) {
        const m = byBranch.get(bid)!;
        m.expenses += Number(e.amount ?? 0);
        byBranch.set(bid, m);
      }
    });
    deadStock.forEach((i) => {
      const bid = (i as { branch_id?: string | null }).branch_id ?? "";
      if (byBranch.has(bid)) {
        const m = byBranch.get(bid)!;
        m.deadStockCount += 1;
        m.deadStockValue += i.current_stock * (i.buying_price ?? 0);
        byBranch.set(bid, m);
      }
    });
    inventoryWithCost.forEach((i) => {
      const bid = (i as { branch_id?: string | null }).branch_id ?? "";
      if (byBranch.has(bid)) {
        const m = byBranch.get(bid)!;
        m.itemCount += 1;
        byBranch.set(bid, m);
      }
    });
    return Array.from(byBranch.entries())
      .filter(([, m]) => m.revenue > 0 || m.expenses > 0 || m.itemCount > 0)
      .map(([branchId, m]) => ({ branchId, branchName: branchIdToName.get(branchId) ?? "Unknown", ...m }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [selectedBranchId, branches, currentSales, currentExpenses, deadStock, inventoryWithCost]);

  const performanceScore = useMemo(() => {
    const hasPrevious = previousRevenue > 0 || previousExpTotal > 0;
    if (!hasPrevious) {
      if (currentRevenue > 0 && currentProfit > 0) return Math.min(100, 55 + Math.round(Math.min(currentMargin, 30)));
      if (currentRevenue > 0 && currentProfit <= 0) return Math.max(0, 45 + Math.round(currentMargin / 2));
      return 50;
    }
    const marginDelta = currentMargin - previousMargin;
    const base = 50;
    const score = Math.round(base + marginDelta * 1.5);
    return Math.min(100, Math.max(0, score));
  }, [currentMargin, previousMargin, previousRevenue, previousExpTotal, currentRevenue, currentProfit]);

  const cashFlowData = useMemo(() => {
    const byBucket: Record<string, { income: number; expenses: number; cashFlow: number; netFlowPositive: number; netFlowNegative: number; label: string; date: string }> = {};
    const byMonth = timeRange === "3M" || timeRange === "6M" || timeRange === "1Y" || timeRange === "All";
    const keyLen = byMonth ? 7 : 10;
    const label = (k: string) => {
      if (byMonth) {
        const d = new Date(k + "-01");
        return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
      }
      return new Date(k).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    };
    currentSales.forEach((s) => {
      const key = s.created_at.slice(0, keyLen);
      if (!byBucket[key]) byBucket[key] = { income: 0, expenses: 0, cashFlow: 0, netFlowPositive: 0, netFlowNegative: 0, label: label(key), date: key };
      byBucket[key].income += saleAmount(s);
    });
    currentExpenses.forEach((e) => {
      const key = (e.expense_date || "").slice(0, keyLen);
      if (!byBucket[key]) byBucket[key] = { income: 0, expenses: 0, cashFlow: 0, netFlowPositive: 0, netFlowNegative: 0, label: label(key), date: key };
      byBucket[key].expenses += Number(e.amount ?? 0);
    });
    Object.keys(byBucket).forEach((k) => {
      const cf = byBucket[k].income - byBucket[k].expenses;
      byBucket[k].cashFlow = cf;
      byBucket[k].netFlowPositive = Math.max(0, cf);
      byBucket[k].netFlowNegative = Math.min(0, cf);
    });
    const sorted = [...Object.values(byBucket)].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    if (sorted.length === 0) {
      const start = bounds.currentStart;
      const end = bounds.currentEnd;
      const startKey = start.toISOString().slice(0, keyLen);
      const endKey = end.toISOString().slice(0, keyLen);
      return [
        { income: 0, expenses: 0, cashFlow: 0, netFlowPositive: 0, netFlowNegative: 0, label: label(startKey), date: startKey },
        { income: 0, expenses: 0, cashFlow: 0, netFlowPositive: 0, netFlowNegative: 0, label: label(endKey), date: endKey },
      ];
    }
    return sorted;
  }, [currentSales, currentExpenses, timeRange, bounds.currentStart, bounds.currentEnd]);

  const topProducts = useMemo(() => {
    const byName: Record<string, number> = {};
    currentSales.forEach((s) => {
      const name = s.item_name || "Unknown";
      byName[name] = (byName[name] || 0) + (s.quantity || 0);
    });
    const list = Object.entries(byName)
      .map(([name, qty]) => ({ name, qty }))
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 5);
    const max = Math.max(1, ...list.map((x) => x.qty));
    return list.map((x) => ({ ...x, pct: (x.qty / max) * 100 }));
  }, [currentSales]);

  const criticalItems = inventory.filter((i) => i.current_stock < (i.min_threshold ?? 5));

  const inventoryValueAtCost = useMemo(
    () => inventoryWithCost.reduce((s, i) => s + i.current_stock * (i.buying_price ?? 0), 0),
    [inventoryWithCost]
  );

  const cardClass = "backdrop-blur-md bg-background/80 border border-border/60 shadow-sm rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200";

  const CURRENCY = "K";
  const dashboardSummary = useMemo(() => {
    const rev = loading ? "—" : `${CURRENCY} ${currentRevenue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const exp = loading ? "—" : `${CURRENCY} ${currentExpTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const profit = loading ? "—" : `${CURRENCY} ${currentProfit.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const invVal = loading ? "—" : `${CURRENCY} ${inventoryValueAtCost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const top = topProducts.map((p) => `${p.name}: ${p.qty} sold`).join("; ") || "No sales yet";
    const revChange = formatPct(revPct);
    const expChange = formatPct(expPct);
    const profitChange = formatPct(profitPct);
    return `Time range: ${timeRange}. Total Revenue: ${rev} (${revChange} vs previous period). Total Expenses: ${exp} (${expChange} vs previous period). Net Profit: ${profit} (${profitChange} vs previous period). Inventory value at cost (safety buffer): ${invVal} (current stock × buying_price). Number of sales: ${currentSales.length}. Top products by quantity: ${top}. Dead stock items: ${deadStock.length}.`;
  }, [timeRange, currentRevenue, currentExpTotal, currentProfit, inventoryValueAtCost, deadStock.length, currentSales.length, topProducts, loading, revPct, expPct, profitPct]);

  const consolidatedCashFlowConfig = {
    income: { label: "Income", color: INCOME_COLOR },
    expenses: { label: "Expenses", color: EXPENSE_COLOR },
    cashFlow: { label: "Net Cash Flow", color: NET_FLOW_COLOR },
  };

  return (
    <div data-testid="dashboard-main" className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          {firstName ? (
            <h2 className="text-2xl font-display font-bold">
              Good {greeting}, {firstName}! 👋
            </h2>
          ) : (
            <h2 className="text-2xl font-display font-bold">Business Intelligence Analyst</h2>
          )}
          <p className="text-sm text-muted-foreground">
            {currentTime} · Track key KPIs and trends without exposing store-wide totals
          </p>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/60 border border-border">
          {TIME_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setTimeRange(opt.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${timeRange === opt.value ? "bg-background text-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <AITipCard />

      {selectedBranchId === "all" && branchComparisonMetrics.length > 0 && (
        <Card className={`${cardClass} border-primary/20`}>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" />
              Branch Comparison
            </CardTitle>
            <p className="text-xs text-muted-foreground">How each location is performing in this period</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 font-medium">Branch</th>
                    <th className="text-right py-2 font-medium">Revenue</th>
                    <th className="text-right py-2 font-medium">Expenses</th>
                    <th className="text-right py-2 font-medium">Items</th>
                    <th className="text-right py-2 font-medium">Dead Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {branchComparisonMetrics.map((m) => (
                    <tr key={m.branchId} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-2 font-medium">{m.branchName}</td>
                      <td className="text-right py-2">{CURRENCY} {m.revenue.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                      <td className="text-right py-2">{CURRENCY} {m.expenses.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                      <td className="text-right py-2">{m.itemCount}</td>
                      <td className="text-right py-2">
                        {m.deadStockCount} {isPro ? `(${CURRENCY} ${m.deadStockValue.toLocaleString("en-US", { minimumFractionDigits: 0 })})` : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-3">Select a branch in the header to view only that location.</p>
          </CardContent>
        </Card>
      )}

      {/* Consolidated Cash Flow — Income, Expenses, Net Flow, zero baseline, negative burn highlight */}
      <Card className={`${cardClass}`}>
        <CardHeader className="pb-1">
          <CardTitle className="text-base">Consolidated Cash Flow</CardTitle>
          <p className="text-xs text-muted-foreground">Income, expenses, and net cash flow by period. Hover to see all three values for each day.</p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-[300px] flex items-center justify-center">
              <span className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <ChartContainer id="consolidated-cashflow" config={consolidatedCashFlowConfig} className="h-[300px] w-full">
              <ComposedChart data={cashFlowData} margin={{ left: 12, right: 24, top: 16, bottom: 52 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} dy={8} />
                <YAxis
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  width={72}
                  tickFormatter={(v: number) => {
                    if (Math.abs(v) >= 1000) return `${CURRENCY} ${(v / 1000).toFixed(1)}k`;
                    return `${CURRENCY} ${v.toLocaleString()}`;
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    padding: "10px 14px",
                    fontSize: "12px",
                    color: "hsl(var(--popover-foreground))",
                  }}
                  labelStyle={{ fontWeight: 600, marginBottom: 6 }}
                  labelFormatter={(label) => label}
                  formatter={(value: number, name: string) => [
                    `${CURRENCY} ${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                    name === "income" ? "Income" : name === "expenses" ? "Expenses" : "Net Cash Flow",
                  ]}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length || !label) return null;
                    const p = payload[0]?.payload as { income: number; expenses: number; cashFlow: number };
                    if (!p) return null;
                    const isNegative = p.cashFlow < 0;
                    return (
                      <div className="rounded-lg border bg-popover px-3 py-2.5 shadow-md">
                        <p className="font-semibold text-foreground mb-2">{label}</p>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between gap-4">
                            <span className="text-muted-foreground">Income</span>
                            <span className="font-medium" style={{ color: INCOME_COLOR }}>{CURRENCY} {p.income.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span className="text-muted-foreground">Expenses</span>
                            <span className="font-medium" style={{ color: EXPENSE_COLOR }}>{CURRENCY} {p.expenses.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span className="text-muted-foreground">Net Cash Flow</span>
                            <span className={`font-semibold ${isNegative ? "text-red-600" : ""}`}>{CURRENCY} {p.cashFlow.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          {isNegative && (
                            <p className="text-[11px] text-red-600 dark:text-red-400 mt-1.5 pt-1 border-t border-border/50">Negative burn — more expenses than income this period.</p>
                          )}
                        </div>
                      </div>
                    );
                  }}
                />
                <ReferenceLine y={0} stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="4 2" />
                <Area type="monotone" dataKey="netFlowPositive" stroke="none" fill={NET_FLOW_FILL} name="Net (positive)" isAnimationActive={false} />
                <Area type="monotone" dataKey="netFlowNegative" stroke="none" fill={NEGATIVE_BURN_FILL} name="Negative burn" isAnimationActive={false} />
                <Line type="monotone" dataKey="income" stroke={INCOME_COLOR} strokeWidth={2} dot={{ r: 3, fill: INCOME_COLOR }} activeDot={{ r: 5 }} name="Income" />
                <Line type="monotone" dataKey="expenses" stroke={EXPENSE_COLOR} strokeWidth={2} dot={{ r: 3, fill: EXPENSE_COLOR }} activeDot={{ r: 5 }} name="Expenses" />
                <Line type="monotone" dataKey="cashFlow" stroke={NET_FLOW_COLOR} strokeWidth={3} strokeDasharray="0" dot={{ r: 3, fill: NET_FLOW_COLOR }} activeDot={{ r: 5 }} name="Net Cash Flow" />
                <Legend verticalAlign="bottom" height={36} iconType="line" wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }} layout="horizontal" align="center" />
              </ComposedChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>

      {/* Stock Intelligence: Survival Days (Free) + Dead Stock (Free: names; Pro: Kwacha) + Inventory Value (Pro) */}
      <Card id="stock-intelligence" data-testid="survival-days-widget" className={`${cardClass} border-amber-500/30 hover:bg-amber-500/5 overflow-visible`}>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              <Package className="h-4 w-4 text-amber-600" />
              Stock Intelligence
              {!isPro && (
                <Badge variant="secondary" className="text-[9px] px-1.5 py-0 ml-1">Free</Badge>
              )}
              {isPro && (
                <Badge variant="default" className="text-[9px] px-1.5 py-0 ml-1">Pro</Badge>
              )}
            </CardTitle>
            <Link
              to="/stock-intelligence"
              data-testid="stock-intelligence-full-details-link"
              className="text-xs font-medium text-primary hover:underline shrink-0"
            >
              Full Stock intelligence →
            </Link>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Dead stock (0 sales in 30 days) and inventory value. {isPro ? "Deeper margin, reorder, and liquidation detail is on Stock intelligence." : "Upgrade to Pro for Kwacha values and the full Stock intelligence page."}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {inventoryWithCost.length < 5 ? (
            <p data-testid="survival-days-empty-state" className="text-sm text-muted-foreground">Add more products to unlock your Survival Days progress.</p>
          ) : (
          <div className="flex flex-wrap gap-4">
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase">Survival Days</p>
              {!isPro ? (
                <button
                  type="button"
                  data-testid="survival-days-breakdown-arrow"
                  onClick={() => openPaywall("survival_days")}
                  className="text-left"
                >
                  <p data-testid="survival-days-number" className="text-lg font-bold tabular-nums">
                    {paywallPreview ? paywallPreview.survivalDays : "—"} days
                  </p>
                  <p data-testid="survival-days-progress-prompt" className="text-[10px] text-muted-foreground">Cash runway at current burn rate — tap for breakdown</p>
                </button>
              ) : (
                <div data-testid="survival-days-full-breakdown">
                  <p data-testid="survival-days-number" className="text-lg font-bold tabular-nums">
                    {paywallPreview ? paywallPreview.survivalDays : "—"} days
                  </p>
                  <p className="text-[10px] text-muted-foreground">Cash runway at current burn rate</p>
                </div>
              )}
            </div>
            {isPro && (
              <div>
                <p className="text-[11px] font-medium text-muted-foreground uppercase">Inventory Value (at Cost)</p>
                <p className="text-lg font-bold tabular-nums">
                  {loading ? "—" : `${CURRENCY} ${inventoryValueAtCost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </p>
                <p className="text-[10px] text-muted-foreground">current_stock × buying_price</p>
              </div>
            )}
            <div data-testid="dead-stock-alert" data-empty={deadStock.length === 0 ? "true" : undefined}>
              <p className="text-[11px] font-medium text-muted-foreground uppercase">Dead Stock (30d)</p>
              <p className={`text-lg font-bold tabular-nums ${deadStock.length === 0 ? "text-emerald-600 dark:text-emerald-400" : ""}`}>{deadStock.length} items</p>
              {isPro && deadStock.length > 0 && (
                <p data-testid="dead-stock-kwacha-value" className="text-sm font-semibold text-red-600 dark:text-red-400">
                  {CURRENCY} {deadStock.reduce((s, i) => s + i.current_stock * (i.buying_price ?? 0), 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </p>
              )}
              {isPro && deadStock.length === 0 && (
                <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">K0 — All stock moving</p>
              )}
              {!isPro && deadStock.length > 0 && (
                <button
                  type="button"
                  onClick={() => openPaywall("dead_stock_kwacha")}
                  className="text-sm font-semibold text-primary hover:underline"
                >
                  See Kwacha value →
                </button>
              )}
              <p className="text-[10px] text-muted-foreground">No sales in last 30 days</p>
              {deadStock.length === 0 && <span data-testid="dead-stock-empty-state" className="sr-only">No dead stock</span>}
              {deadStock.length > 0 && (
                <Link
                  to={`/inventory?deadStock=${deadStock.map((i) => i.id).join(",")}`}
                  className="text-xs font-medium text-primary hover:underline mt-1 inline-block"
                >
                  Details → adjust prices
                </Link>
              )}
            </div>
          </div>
          )}
          {plan === "pro_growth" && branches.length > 1 && (
            <div className="pt-3 border-t border-border/50">
              <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                <MapPin className="h-3 w-3" /> Locations at a glance
              </p>
              <div className="flex flex-wrap gap-2">
                {branches.map((b) => {
                  const stat = branchStats.find((s) => s.branchId === b.id);
                  const itemCount = stat?.itemCount ?? 0;
                  const criticalCount = stat?.criticalCount ?? 0;
                  return (
                    <Link
                      key={b.id}
                      to={`/inventory?branch=${b.id}`}
                      className="inline-flex items-center gap-2 rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-sm hover:bg-muted/70"
                    >
                      <span className="font-medium">{b.name}</span>
                      <span className="text-muted-foreground">{itemCount} items</span>
                      {criticalCount > 0 && (
                        <Badge variant="destructive" className="text-[10px] px-1.5 py-0">{criticalCount} critical</Badge>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
          {inventoryWithCost.length >= 5 && deadStock.length > 0 && (
            <div data-testid="dead-stock-recovery-plan" className="pt-2 border-t border-border/50">
              <p className="text-xs font-medium text-muted-foreground mb-1">Dead stock items</p>
              <div className="flex flex-wrap gap-1">
                {deadStock.slice(0, 12).map((i) => (
                  <span key={i.id} data-testid="dead-stock-item-name" className="text-xs px-2 py-0.5 rounded bg-muted">
                    {i.name}
                  </span>
                ))}
                {deadStock.length > 12 && <span className="text-xs text-muted-foreground">+{deadStock.length - 12} more</span>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* The Analyst — Pro only; Free users see upgrade prompt */}
      <div>
        <AIInsights timeRange={timeRange} dashboardSummary={dashboardSummary} isPro={isPro} inventoryCount={inventoryWithCost.length} />
      </div>

      {criticalItems.length > 0 && (
        <Link to="/inventory">
          <Card className="backdrop-blur-md bg-background/80 border border-red-500/30 shadow-sm rounded-xl hover:shadow-lg hover:-translate-y-0.5 hover:bg-red-500/5 transition-all duration-200 cursor-pointer">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <AlertTriangle className="h-5 w-5" />
                Low Stock Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {criticalItems.slice(0, 8).map((item, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center py-2 px-3 rounded-md bg-card border border-red-500/20"
                  >
                    <span className="font-medium text-sm">{item.name}</span>
                    <span className="text-red-600 dark:text-red-400 font-bold text-sm ml-2">{item.current_stock} units</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </Link>
      )}
    </div>
  );
}
